/**
 * TimeEntryRow Component
 *
 * Displays a time entry in row format using BaseRow layout.
 * Shows user, duration, billable status, and cost.
 */

import React, { useMemo, useState } from 'react';
import { Clock } from 'lucide-react';
import { BaseRow } from '../common/base/BaseRow';
import { PrimaryAction, EntityAvatar } from '../common/base';
import { LinkManager } from '../../services/actions/LinkManager';
import { getAvailableActions } from '../common/registry/ActionRegistry';
import { useTranslation } from '../../hooks';
import type { TimeEntry } from '@business-assistant/shared/domain';

// =============================================================================
// TYPES
// =============================================================================

export interface TimeEntryRowProps {
  entity: TimeEntry;
  onTitleClick?: (timeEntry: TimeEntry) => void;
  onActionClick?: (actionType: string, timeEntryId: string) => void;
  onAvatarClick?: (name: string, id?: string) => void;
}

// =============================================================================
// COMPONENT
// =============================================================================

export function TimeEntryRow({ entity: timeEntry, onTitleClick, onActionClick, onAvatarClick }: TimeEntryRowProps) {
  const { t, language } = useTranslation();
  const locale = language === 'it' ? 'it-IT' : 'en-US';

  // Locale-aware date formatting
  const formatDate = (isoDate: string): string => {
    const date = new Date(isoDate);
    return date.toLocaleDateString(locale, {
      day: '2-digit',
      month: 'short',
    });
  };

  // Primary action state (reviewed/acknowledged)
  const [isReviewed, setIsReviewed] = useState(false);

  // Get linked action counts for icon coloring
  const linkedCounts = useMemo(() => {
    return LinkManager.getLinkedActionCounts(timeEntry.id);
  }, [timeEntry.id]);

  // Left slot - Primary Action + User Avatar
  const leftSlot = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <PrimaryAction
        type="eye"
        active={isReviewed}
        onChange={(reviewed) => {
          setIsReviewed(reviewed);
          onActionClick?.('reviewed', timeEntry.id);
        }}
      />
      <EntityAvatar
        items={timeEntry.userName ? { id: timeEntry.userId, name: timeEntry.userName } : []}
        type="person"
        size={24}
        onClick={onAvatarClick ? (item) => onAvatarClick(item.name, item.id) : undefined}
      />
    </div>
  );

  // Get available actions for time entries
  const availableActions = useMemo(() => {
    return getAvailableActions(timeEntry.id);
  }, [timeEntry.id]);

  // Build title
  const title = useMemo(() => {
    if (timeEntry.description) {
      return timeEntry.description;
    }
    return `${formatDuration(timeEntry.minutes)} - ${timeEntry.userName || t('timeEntries.noName')}`;
  }, [timeEntry, t]);

  // Build meta content
  const metaContent = useMemo(() => {
    const parts: React.ReactNode[] = [];

    // User name (if description is title)
    if (timeEntry.description && timeEntry.userName) {
      parts.push(
        <span key="user" style={styles.user}>
          {timeEntry.userName}
        </span>
      );
    }

    // Duration
    parts.push(
      <span key="duration" style={styles.duration}>
        {formatDuration(timeEntry.minutes)}
      </span>
    );

    // Billable status
    parts.push(
      <span
        key="billable"
        style={{
          ...styles.billableBadge,
          ...(timeEntry.billable ? styles.billableYes : styles.billableNo),
        }}
      >
        {timeEntry.billable ? t('timeEntries.billableStatus') : t('timeEntries.nonBillable')}
      </span>
    );

    // Cost and billing amounts
    const costAmount = ((timeEntry.costRate / 100) * (timeEntry.minutes / 60)).toFixed(2);
    const billableAmount = ((timeEntry.billableRate / 100) * (timeEntry.minutes / 60)).toFixed(2);

    if (timeEntry.billable) {
      parts.push(
        <span key="amount" style={styles.amount}>
          €{billableAmount}
        </span>
      );
    } else {
      parts.push(
        <span key="cost" style={styles.cost}>
          {t('timeEntries.costLabel')} €{costAmount}
        </span>
      );
    }

    // Date
    parts.push(
      <span key="date" style={styles.date}>
        {formatDate(timeEntry.at)}
      </span>
    );

    return (
      <div style={styles.metaContainer}>
        {parts.map((part, idx) => (
          <React.Fragment key={idx}>
            {idx > 0 && <span style={styles.separator}>·</span>}
            {part}
          </React.Fragment>
        ))}
      </div>
    );
  }, [timeEntry, t, formatDate]);

  // Icon
  const icon = (
    <Clock
      size={16}
      style={{ color: timeEntry.billable ? '#16a34a' : '#9ca3af' }}
    />
  );

  return (
    <BaseRow
      entity={{ ...timeEntry, title }}
      entityType="timeEntry"
      leftSlot={leftSlot}
      icon={icon}
      metaContent={metaContent}
      availableActions={availableActions}
      linkedActionCounts={linkedCounts}
      onTitleClick={onTitleClick}
      onActionClick={onActionClick}
      done={isReviewed}
    />
  );
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hours > 0) {
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
  return `${mins}m`;
}

// formatDate is now handled inside the component with locale awareness

// =============================================================================
// STYLES
// =============================================================================

const styles = {
  metaContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    flexWrap: 'wrap' as const,
  } as React.CSSProperties,
  separator: {
    color: '#ccc',
    margin: '0 2px',
  } as React.CSSProperties,
  user: {
    color: '#666',
    fontWeight: 500,
  } as React.CSSProperties,
  duration: {
    color: '#3b82f6',
    fontWeight: 600,
  } as React.CSSProperties,
  billableBadge: {
    padding: '2px 6px',
    borderRadius: '3px',
    fontSize: '11px',
    fontWeight: 500,
  } as React.CSSProperties,
  billableYes: {
    background: '#dcfce7',
    color: '#16a34a',
  } as React.CSSProperties,
  billableNo: {
    background: '#f3f4f6',
    color: '#6b7280',
  } as React.CSSProperties,
  amount: {
    color: '#16a34a',
    fontWeight: 600,
  } as React.CSSProperties,
  cost: {
    color: '#999',
    fontSize: '11px',
  } as React.CSSProperties,
  date: {
    color: '#999',
  } as React.CSSProperties,
};
